import * as React from 'react'
import clsx from 'clsx'
import { StepperContextType, useStepperContext } from '../Stepper'
import { SxProps, sx as sxFunc } from '../sx'
import style from './index.module.scss'
import { StepContext } from './StepContext'

export interface StepClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the root element if `orientation="horizontal"`. */
  horizontal: string
  /** Styles applied to the root element if `orientation="vertical"`. */
  vertical: string
  /** Styles applied to the root element if `alternativeLabel={true}`. */
  alternativeLabel: string
  /** State class applied to the root element if `completed={true}`. */
  completed: string
  /** State class applied to the root element if `active={true}`. */
  active: string
  /** State class applied to the root element if `disabled={true}`. */
  disabled: string
}

export interface StepProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Sets the step as active. Is passed to child components.
   */
  active?: boolean
  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   * @default div
   */
  Component?: React.ElementType
  /**
   * Should be `Step` sub-components such as `StepLabel`, `StepContent`.
   */
  children?: React.ReactNode
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<StepClasses>
  /**
   * Mark the step as completed. Is passed to child components.
   */
  completed?: boolean
  /**
   * If `true`, the step is disabled, will also disable the button if
   * `StepButton` is a child of `Step`. Is passed to child components.
   */
  disabled?: boolean
  /**
   * Expand the step.
   * @default false
   */
  expanded?: boolean
  /**
   * The position of the step.
   * The prop defaults to the value inherited from the parent Stepper component.
   */
  index?: number
  /**
   * If `true`, the Step is displayed as rendered last.
   * The prop defaults to the value inherited from the parent Stepper component.
   */
  last?: boolean
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * internal props
   */
  variant?: StepperContextType['variant']
  dashStyle?: StepperContextType['dashStyle']
}

export const Step = React.forwardRef<HTMLDivElement, StepProps>(
  (props, ref) => {
    const {
      active: activeProp,
      classes,
      children,
      className,
      Component = 'div',
      completed: completedProp,
      disabled: disabledProp,
      expanded = false,
      index = 0,
      last,
      variant: variantProp,
      dashStyle: dashStyleProp,
      sx,
      ...other
    } = props

    const {
      activeStep,
      connector,
      alternativeLabel,
      orientation,
      nonLinear,
      variant: variantContext,
      dashStyle: dashStyleContext,
    } = useStepperContext() as StepperContextType

    let [active = false, completed = false, disabled = false] = [
      activeProp,
      completedProp,
      disabledProp,
    ]

    if (activeStep === index) {
      active = activeProp !== undefined ? activeProp : true
    } else if (!nonLinear && activeStep > index) {
      completed = completedProp !== undefined ? completedProp : true
    } else if (!nonLinear && activeStep < index) {
      disabled = disabledProp !== undefined ? disabledProp : true
    }

    const variant = variantContext ?? variantProp
    const dashStyle = dashStyleContext ?? dashStyleProp
    const dashInlineStyle =
      variant === 'dash' &&
      ({
        '--width': dashStyle.width,
        '--height': dashStyle.height,
        '--gap': dashStyle.gap,
      } as React.CSSProperties)

    const contextValue = React.useMemo(
      () => ({
        index,
        last,
        expanded,
        icon: index + 1,
        active,
        completed,
        disabled,
      }),
      [index, last, expanded, active, completed, disabled]
    )

    const newChildren = (
      <Component
        ref={ref}
        className={clsx(
          variant === 'standard' ? style.standard : style.dash,
          classes?.root,
          style?.[`orientation-${orientation}`],
          classes?.[orientation],
          alternativeLabel && [
            style.alternativeLabel,
            classes?.alternativeLabel,
          ],
          active && [classes?.active, style.active],
          completed && [classes?.completed, style.completed],
          disabled && [classes?.disabled, style.disabled],
          className,
          sxFunc(sx)
        )}
        style={{ ...other.style, ...dashInlineStyle }}
        {...other}
      >
        {connector && alternativeLabel && index !== 0 ? connector : null}
        {children}
      </Component>
    )

    return (
      <StepContext.Provider value={contextValue}>
        {connector && !alternativeLabel && index !== 0 ? (
          <React.Fragment>
            {connector}
            {newChildren}
          </React.Fragment>
        ) : (
          newChildren
        )}
      </StepContext.Provider>
    )
  }
)

Step.displayName = 'Step'
